import React, { useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons';

import { useForm } from '../../hooks/useForm';
import {
  useFetchInvitations,
  useExportInvitations,
} from '../../services/invitations';
import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';
import DataTable from '../common/DataTable';
import Pagination from '../common/Pagination';
import dayjs from '../../lib/day';
import { ucwords } from '../../lib/strings';
import { useUser } from '../../contexts/UserContext';

const Invitations = () => {
  const { form, onChange } = useForm({ search: '' });
  const [filters, setFilters] = useState({
    page: 1,
    search: undefined,
    sortBy: 'inviteDate',
    sortDir: 'DESC',
  });
  const { hasPermission } = useUser();

  const { isLoading, data } = useFetchInvitations(
    filters.page,
    filters.search,
    filters.sortBy,
    filters.sortDir,
  );

  const exportInvitations = useExportInvitations(
    (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invitations.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    {
      onError: (err) => toast.error(err.message),
    },
  );

  const onSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: form.search });
  };

  const handleExport = () => {
    exportInvitations.mutate();
  };

  // Permission-based check for viewing all invitations (and export)
  const canViewAllInvitations = hasPermission('invitations:view_all');

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>Client Invitations</h2>
              </Col>
              <Col xs={6} sm={3} className="text-end">
                {/* Export button (invitations:view_all permission required) */}
                {canViewAllInvitations && (
                  <Button variant="outline-primary me-2" onClick={handleExport}>
                    <FontAwesomeIcon icon={faFileCsv} /> Export
                  </Button>
                )}
                {/* Read-only history (Nic 2026-09-22): no Send or Resend.
                    Signup links only go out from a lead on the Onboarding
                    Support board, so nobody ends up with both a lead and a
                    client account. */}
              </Col>
              <Col xs={12} sm={4} className="text-end">
                <form onSubmit={onSubmit}>
                  <Form.Control
                    name="search"
                    value={form.search}
                    type="search"
                    placeholder="Search..."
                    onChange={onChange}
                  ></Form.Control>
                </form>
              </Col>
            </Row>
            <br />
            <DataTable
              columns={[
                {
                  header: 'Email',
                  key: 'inviteEmail',
                  sortable: true,
                },
                {
                  header: 'Sent By',
                  key: 'userLastName',
                  sortable: true,
                  render: (row) => `${row.userLastName}, ${row.userFirstName}`,
                },
                {
                  header: 'Sent On',
                  key: 'inviteDate',
                  sortable: true,
                  render: (row) =>
                    dayjs.utc(row.inviteDate).format('MM/DD/YYYY'),
                },
                {
                  header: 'Status',
                  key: 'inviteStatus',
                  sortable: true,
                  render: (row) => ucwords(row.inviteStatus),
                },
              ]}
              busy={isLoading}
              data={data ? data.rows : []}
              defaultSort={`${filters.sortBy}:${filters.sortDir}`}
              onSortChange={(newSortBy, newSortDir) => {
                setFilters({
                  ...filters,
                  page: 1,
                  sortBy: newSortBy,
                  sortDir: newSortDir,
                });
              }}
            />

            {data && data.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={data.total}
                rowsPerPage={50}
                onBack={() =>
                  setFilters({ ...filters, page: filters.page - 1 })
                }
                onForward={() =>
                  setFilters({ ...filters, page: filters.page + 1 })
                }
              />
            ) : null}
          </Panel>
        </Col>
      </Row>
    </PrivateLayout>
  );
};

export default Invitations;
